import React, { useState, useEffect, useRef } from 'react';
import { reportsAPI, vendorsAPI, vendorInvoicesAPI, departmentsAPI } from '../services/api';
import { Vendor, Department } from '../types';
import { formatCurrency, getCurrencyFontClass } from '../utils/currency';
import Pagination from '../components/Pagination';
import Spinner from '../components/Spinner';
import {
  FileText,
  Eye,
  ChevronDown,
  X,
  Download,
  FileSpreadsheet,
  Filter,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const getNextIsoDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!isoDate || [year, month, day].some((value) => Number.isNaN(value))) {
    return isoDate;
  }
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1));
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

interface DetailedVendorInvoice {
  id: number;
  vendor_id: number;
  vendor_name?: string;
  vendor_email?: string;
  vendor_phone?: string;
  vendor_address?: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  status: string;
  currency: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  paid_amount: number;
  description?: string;
  notes?: string;
  po_number?: string;
  registration_number?: string;
  attachment_url?: string;
  additional_attachments?: Array<{ filename: string; filepath: string; url?: string }>;
  line_items?: Array<{
    id: number;
    description: string;
    quantity: number;
    unit_price: number;
    amount: number;
  }>;
  bank_details?: {
    bank_name: string;
    account_holder_name: string;
    account_number: string;
    account_type: string;
    ifsc_code: string;
  };
  audit_logs?: Array<{
    id: number;
    action: string;
    user: {
      id: number;
      email: string;
      first_name: string;
      last_name: string;
    };
    changes: any;
    ip_address?: string;
    user_agent?: string;
    created_at: string;
  }>;
  department_name?: string;
  department_id?: number;
}

const Reports: React.FC = () => {
  // Applied filters (used in API calls)
  const [appliedVendorFilter, setAppliedVendorFilter] = useState<number | undefined>(undefined);
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>('all');
  const [appliedStartDate, setAppliedStartDate] = useState<string>('');
  const [appliedEndDate, setAppliedEndDate] = useState<string>('');
  const [appliedDepartmentFilter, setAppliedDepartmentFilter] = useState<number | undefined>(undefined);
  const [appliedMaxAmount, setAppliedMaxAmount] = useState<number>(1000000);
  const [appliedAmountFilterEnabled, setAppliedAmountFilterEnabled] = useState<boolean>(false);

  // Temporary filters (edited in the panel before applying)
  const [vendorFilter, setVendorFilter] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [departmentFilter, setDepartmentFilter] = useState<number | undefined>(undefined);
  const [maxAmount, setMaxAmount] = useState<number>(1000000);
  const [amountFilterEnabled, setAmountFilterEnabled] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<DetailedVendorInvoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<DetailedVendorInvoice[]>([]);
  const [auditTrailData, setAuditTrailData] = useState<any>(null);
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState<string>('');
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string>('');
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [originalAttachmentUrl, setOriginalAttachmentUrl] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Dropdown states
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const [allVendors, setAllVendors] = useState<Vendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Department dropdown states
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const departmentDropdownRef = useRef<HTMLDivElement>(null);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Status dropdown states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearchTerm, setStatusSearchTerm] = useState('');
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch vendors based on department selection
  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        if (departmentFilter && departmentFilter > 0) {
          // Fetch vendors for specific department
          const response = await vendorsAPI.dropdown(departmentFilter);
          if (response.success && response.data && Array.isArray(response.data)) {
            setAllVendors(response.data);
            
            // Clear vendor selection if current vendor is not in the filtered list
            if (vendorFilter) {
              const vendorExists = response.data.some((v: Vendor) => v.id === vendorFilter);
              if (!vendorExists) {
                setVendorFilter(undefined);
              }
            }
          } else {
            setAllVendors([]);
          }
        } else {
          // Fetch all vendors when no department is selected
        let allVendorsList: Vendor[] = [];
        let currentPage = 1;
        let hasMore = true;
        const pageLimit = 100;

          while (hasMore && currentPage <= 100) {
          const response = await vendorsAPI.getAll({ 
            page: currentPage, 
            limit: pageLimit,
            order: 'asc' 
          });
          
          if (response.success && response.data && Array.isArray(response.data)) {
            allVendorsList = [...allVendorsList, ...response.data];
            
            if (response.pagination) {
              hasMore = currentPage < response.pagination.total_pages;
              currentPage++;
            } else if (response.data.length === pageLimit) {
              currentPage++;
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        setAllVendors(allVendorsList);
        }
      } catch (error) {
        console.error('Failed to fetch vendors:', error);
        errorModal('Failed to load vendors');
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, [departmentFilter]);

  // Fetch all departments for dropdown
  useEffect(() => {
    const fetchAllDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const response = await departmentsAPI.dropDown();
        if (response.success && response.data && Array.isArray(response.data)) {
          setAllDepartments(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        errorModal('Failed to load departments');
      } finally {
        setLoadingDepartments(false);
      }
    };
    fetchAllDepartments();
  }, []);

  // Filter vendors based on search term
  const filteredVendors = allVendors.filter((vendor) => {
    const vendorName = vendor?.name ?? '';
    return vendorName
      .toString()
      .toLowerCase()
      .includes(vendorSearchTerm.toLowerCase());
  });

  // Filter departments based on search term
  const filteredDepartments = allDepartments.filter((dept) =>
    dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
  );

  // Fetch reports data (using applied filters)
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const params: any = {
          page,
          limit,
        };
        
        // Use applied filters for API calls
        if (appliedVendorFilter && appliedVendorFilter > 0) {
          params.vendor_id = appliedVendorFilter;
        }
        
        if (appliedStatusFilter && appliedStatusFilter !== 'all') {
          params.status = appliedStatusFilter;
        }
        
        if (appliedStartDate) {
          params.start_date = appliedStartDate;
        }
        
        if (appliedEndDate) {
          params.end_date = appliedEndDate;
        }

        if (appliedDepartmentFilter && appliedDepartmentFilter > 0) {
          params.department_id = appliedDepartmentFilter;
        }

        if (appliedAmountFilterEnabled && appliedMaxAmount > 0) {
          params.amount = appliedMaxAmount;
        }

        const response = await reportsAPI.getVendorInvoicesDetailed(params);
        if (response.success) {
          setInvoices(response.data || []);
          if (response.pagination) {
            setPagination(response.pagination);
          }
        } else {
          errorModal(response.error || 'Failed to fetch reports');
        }
      } catch (error: any) {
        console.error('Error fetching reports:', error);
        errorModal(error?.response?.data?.error || 'Failed to fetch reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [page, limit, appliedVendorFilter, appliedStatusFilter, appliedStartDate, appliedEndDate, appliedDepartmentFilter, appliedMaxAmount, appliedAmountFilterEnabled]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target as Node)) {
        setVendorDropdownOpen(false);
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setDepartmentDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset to first page when applied filters change
  useEffect(() => {
    setPage(1);
  }, [appliedVendorFilter, appliedStatusFilter, appliedStartDate, appliedEndDate, appliedDepartmentFilter, appliedMaxAmount, appliedAmountFilterEnabled]);

  const handleViewDetails = async (invoice: DetailedVendorInvoice) => {
    try {
      const response = await reportsAPI.getVendorInvoiceDetailedById(invoice.id);
      if (response.success && response.data) {
        setSelectedInvoice(response.data);
        setShowDetailModal(true);
        
        // Fetch audit trail
        try {
          const auditRes = await vendorInvoicesAPI.getAuditTrail(invoice.id);
          if (auditRes.success) {
            setAuditTrailData(auditRes.data);
          }
        } catch (auditError) {
          console.error('Error fetching status trail:', auditError);
        }
      } else {
        errorModal('Failed to fetch invoice details');
      }
    } catch (error: any) {
      console.error('Error fetching invoice details:', error);
      errorModal('Failed to fetch invoice details');
    }
  };

  const handleClearFilters = () => {
    // Clear temporary filters
    setVendorFilter(undefined);
    setStatusFilter('all');
    setStartDate('');
    setEndDate('');
    setDepartmentFilter(undefined);
    setMaxAmount(1000000);
    setAmountFilterEnabled(false);
    
    // Clear applied filters
    setAppliedVendorFilter(undefined);
    setAppliedStatusFilter('all');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setAppliedDepartmentFilter(undefined);
    setAppliedMaxAmount(1000000);
    setAppliedAmountFilterEnabled(false);
    setPage(1);
  };

  const handleApplyFilters = () => {
    // Copy temporary filter values to applied values
    setAppliedVendorFilter(vendorFilter);
    setAppliedStatusFilter(statusFilter);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedDepartmentFilter(departmentFilter);
    setAppliedMaxAmount(maxAmount);
    setAppliedAmountFilterEnabled(amountFilterEnabled);
    setShowFilterPanel(false);
  };

  const handleOpenFilterPanel = () => {
    // Initialize temporary filters with current applied values
    setVendorFilter(appliedVendorFilter);
    setStatusFilter(appliedStatusFilter);
    setStartDate(appliedStartDate);
    setEndDate(appliedEndDate);
    setDepartmentFilter(appliedDepartmentFilter);
    setMaxAmount(appliedMaxAmount);
    setAmountFilterEnabled(appliedAmountFilterEnabled);
    setShowFilterPanel(true);
  };

  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Finance Team Approved', value: 'level_1_approved' },
    { label: 'Level 2 Approved', value: 'level_2_approved' },
    { label: 'Level 3 Approved', value: 'level_3_approved' },
    { label: 'Paid', value: 'paid' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Overdue', value: 'overdue' },
  ];

  const getStatusBadgeClass = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('approved') || statusLower === 'paid') {
      return 'bg-green-100 text-green-800';
    } else if (statusLower === 'pending' || statusLower.includes('pending')) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (statusLower === 'rejected' || statusLower.includes('rejected')) {
      return 'bg-red-100 text-red-800';
    } else if (statusLower === 'overdue') {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  // Use temporary filters for display in the panel
  const selectedVendorName = allVendors.find((v) => v.id === vendorFilter)?.name || 'All Vendors';
  const selectedDepartmentName = allDepartments.find((d) => d.id === departmentFilter)?.department || 'All Departments';
  const selectedStatusName = statusOptions.find((s) => s.value === statusFilter)?.label || 'All';

  const handleViewAttachment = async (url: string, filename: string, isMain: boolean = false) => {
    setLoadingAttachment(true);
    setCurrentAttachmentName(filename);
    
    try {
      // Always download via API first (if we have selectedInvoice) to ensure proper auth
      // This avoids CORS issues and works with blob URLs in iframe
      if (selectedInvoice) {
        try {
          let response;
          if (isMain) {
            // Download main attachment
            response = await vendorInvoicesAPI.download(selectedInvoice.id);
          } else {
            // Download additional attachment
            response = await vendorInvoicesAPI.downloadAdditionalAttachment(selectedInvoice.id, filename);
          }
          
          const contentType = response.headers?.['content-type'] || response.headers?.['Content-Type'] || 'application/pdf';
          const blob = new Blob([response.data], { type: contentType });
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Remove PDF toolbar by appending #toolbar=0
          const urlWithToolbarRemoved = contentType.includes('pdf') 
            ? `${blobUrl}#toolbar=0` 
            : blobUrl;
          
          setCurrentAttachmentUrl(urlWithToolbarRemoved);
          setOriginalAttachmentUrl(url || ''); // Store original URL for fallback
          setShowAttachmentViewer(true);
        } catch (apiError: any) {
          console.error('API download failed, trying direct URL fetch:', apiError);
          // If API download fails, try fetching the URL directly
          if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
            try {
              const fetchResponse = await fetch(url, {
                method: 'GET',
                mode: 'cors',
                credentials: 'include',
                headers: {
                  'Accept': 'application/pdf,image/*,*/*'
                }
              });
              
              if (fetchResponse.ok) {
                const blob = await fetchResponse.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                
                // Remove PDF toolbar by appending #toolbar=0
                const contentType = fetchResponse.headers.get('content-type') || '';
                const urlWithToolbarRemoved = contentType.includes('pdf') 
                  ? `${blobUrl}#toolbar=0` 
                  : blobUrl;
                
                setCurrentAttachmentUrl(urlWithToolbarRemoved);
                setOriginalAttachmentUrl(url); // Store original URL for fallback
                setShowAttachmentViewer(true);
              } else {
                throw new Error(`Fetch failed: ${fetchResponse.status}`);
              }
            } catch (fetchError: any) {
              console.error('Both API and fetch failed, opening in new tab:', fetchError);
              // If both fail, open in new tab as fallback
              if (url) {
                window.open(url, '_blank');
                toast.success('Opening attachment in new tab');
                setLoadingAttachment(false);
                return;
              } else {
                throw fetchError;
              }
            }
          } else {
            throw apiError;
          }
        }
      } else if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        // For direct URLs when no selectedInvoice, fetch and convert to blob URL
        try {
          const response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
              'Accept': 'application/pdf,image/*,*/*'
            }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Remove PDF toolbar by appending #toolbar=0
            const contentType = response.headers.get('content-type') || '';
            const urlWithToolbarRemoved = contentType.includes('pdf') || filename.toLowerCase().endsWith('.pdf')
              ? `${blobUrl}#toolbar=0` 
              : blobUrl;
            
            setCurrentAttachmentUrl(urlWithToolbarRemoved);
            setOriginalAttachmentUrl(url); // Store original URL for fallback
            setShowAttachmentViewer(true);
          } else {
            throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
          }
        } catch (fetchError: any) {
          console.error('Fetch failed, opening in new tab:', fetchError);
          // If fetch fails, open in new tab as fallback
          window.open(url, '_blank');
          toast.success('Opening attachment in new tab');
          setLoadingAttachment(false);
          return;
        }
      } else {
        throw new Error('No valid URL or invoice selected');
      }
    } catch (error: any) {
      console.error('Error viewing attachment:', error);
      errorModal('Failed to load attachment. Please try again.');
    } finally {
      setLoadingAttachment(false);
    }
  };

  const closeAttachmentViewer = () => {
    // Revoke blob URL if it was created
    if (currentAttachmentUrl && currentAttachmentUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(currentAttachmentUrl);
    }
    setShowAttachmentViewer(false);
    setCurrentAttachmentUrl('');
    setCurrentAttachmentName('');
    setOriginalAttachmentUrl('');
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const params: any = {};
      
      // Use applied filters for export
      if (appliedVendorFilter && appliedVendorFilter > 0) {
        params.vendor_id = appliedVendorFilter;
      }
      
      if (appliedStatusFilter && appliedStatusFilter !== 'all') {
        params.status = appliedStatusFilter;
      }
      
      if (appliedStartDate) {
        params.start_date = appliedStartDate;
      }
      
      if (appliedEndDate) {
        params.end_date = appliedEndDate;
      }

      if (appliedDepartmentFilter && appliedDepartmentFilter > 0) {
        params.department_id = appliedDepartmentFilter;
      }

      if (appliedAmountFilterEnabled && appliedMaxAmount > 0) {
        params.amount = appliedMaxAmount;
      }

      const response = await reportsAPI.exportVendorInvoices(params);
      
      if (response.success && response.data) {
        const invoices = response.data;
        
        // Prepare data for Excel
        const headers = [
          'Invoice Number',
          'Vendor Name',
          'Vendor Email',
          'Department',
          'Invoice Date',
          'Due Date',
          'Status',
          'Amount (Excluding Vat/Tax)',
          'Vat/Tax Amount',
          'Total Amount',
          'Paid Amount',
          'Currency',
          'PO Number',
          'Description',
          'Notes',
          'Created At',
        ];
        
        const rows = invoices.map((invoice: any) => [
          invoice.invoice_number || '',
          invoice.vendor_name || '',
          invoice.vendor_email || '',
          invoice.department_name || '',
          invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '',
          invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '',
          invoice.status || '',
          invoice.subtotal || 0,
          invoice.tax_amount || 0,
          invoice.total_amount || 0,
          invoice.paid_amount || 0,
          invoice.currency || 'USD',
          invoice.po_number || '',
          invoice.description || '',
          invoice.notes || '',
          invoice.created_at ? new Date(invoice.created_at).toLocaleString() : '',
        ]);
        
        // Create worksheet
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        
        // Set column widths
        worksheet['!cols'] = [
          { wch: 15 }, // Invoice Number
          { wch: 20 }, // Vendor Name
          { wch: 25 }, // Vendor Email
          { wch: 15 }, // Department
          { wch: 12 }, // Invoice Date
          { wch: 12 }, // Due Date
          { wch: 20 }, // Status
          { wch: 12 }, // Subtotal
          { wch: 12 }, // Tax Amount
          { wch: 12 }, // Total Amount
          { wch: 12 }, // Paid Amount
          { wch: 8 },  // Currency
          { wch: 15 }, // PO Number
          { wch: 30 }, // Description
          { wch: 30 }, // Notes
          { wch: 20 }, // Created At
        ];
        
        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendor Invoices');
        
        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        
        // Generate filename with date and filters
        const dateStr = new Date().toISOString().split('T')[0];
        let filename = `vendor_invoices_${dateStr}`;
        if (appliedVendorFilter) {
          const vendorName = allVendors.find(v => v.id === appliedVendorFilter)?.name || '';
          filename += `_${vendorName.replace(/\s+/g, '_')}`;
        }
        if (appliedStatusFilter && appliedStatusFilter !== 'all') {
          filename += `_${appliedStatusFilter}`;
        }
        filename += '.xlsx';
        
        saveAs(blob, filename);
        toast.success(`Exported ${invoices.length} invoice(s) to Excel`);
      } else {
        errorModal(response.error || 'Failed to export invoices');
      }
    } catch (error: any) {
      console.error('Error exporting invoices:', error);
      errorModal(error?.response?.data?.error || 'Failed to export invoices');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-600">
            Detailed vendor invoice reports with audit trail.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleOpenFilterPanel}
            className="btn-secondary flex items-center"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {(appliedVendorFilter || appliedStatusFilter !== 'all' || appliedStartDate || appliedEndDate || appliedDepartmentFilter || appliedAmountFilterEnabled) && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary-600 rounded-full">
                {[appliedVendorFilter, appliedStatusFilter !== 'all', appliedStartDate, appliedEndDate, appliedDepartmentFilter, appliedAmountFilterEnabled].filter(Boolean).length}
              </span>
            )}
          </button>
          <button
            onClick={handleExportToExcel}
            disabled={isExporting || loading}
            className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <Spinner size="sm" color="white" />
                <span className="ml-2">Exporting...</span>
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to Excel
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filter Slide-out Panel */}
      {showFilterPanel && (
        <div style={{ margin: '0px !important' }}>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={() => setShowFilterPanel(false)}
          ></div>
          
          {/* Slide-out Panel */}
          <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
            {/* Panel Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Filters</h3>
                <button
                  onClick={() => setShowFilterPanel(false)}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Panel Content */}
            <div className="p-6 space-y-6">
              {/* Department Filter */}
              <div className="relative" ref={departmentDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDepartmentDropdownOpen(!departmentDropdownOpen)}
                  className="w-full input-field flex items-center justify-between"
                >
                  <span className="truncate">
                    {selectedDepartmentName}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>
                {departmentDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div className="p-2">
                      <input
                        type="text"
                        placeholder="Search departments..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={departmentSearchTerm}
                        onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {loadingDepartments ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-500">
                          Loading departments...
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setDepartmentFilter(undefined);
                              setDepartmentDropdownOpen(false);
                              setDepartmentSearchTerm('');
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              departmentFilter === undefined ? 'bg-primary-50 text-primary-700' : ''
                            }`}
                          >
                            All Departments
                          </button>
                          {filteredDepartments.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No departments found
                            </div>
                          ) : (
                            filteredDepartments.map((dept) => (
                              <button
                                key={dept.id}
                                type="button"
                                onClick={() => {
                                  setDepartmentFilter(dept.id);
                                  setDepartmentDropdownOpen(false);
                                  setDepartmentSearchTerm('');
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                  departmentFilter === dept.id ? 'bg-primary-50 text-primary-700' : ''
                                }`}
                              >
                                {dept.department}
                              </button>
                            ))
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* Vendor Filter */}
              <div className="relative" ref={vendorDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vendor
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setVendorDropdownOpen(!vendorDropdownOpen)}
                  className="w-full input-field flex items-center justify-between"
                  title={departmentFilter ? 'Showing vendors from selected department' : 'Showing all vendors'}
                >
                  <span className="truncate">
                    {selectedVendorName}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>
                {vendorDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div className="p-2">
                      <input
                        type="text"
                        placeholder="Search vendors..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={vendorSearchTerm}
                        onChange={(e) => setVendorSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {loadingVendors ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-500">
                          Loading vendors...
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setVendorFilter(undefined);
                              setVendorDropdownOpen(false);
                              setVendorSearchTerm('');
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              vendorFilter === undefined ? 'bg-primary-50 text-primary-700' : ''
                            }`}
                          >
                            All Vendors
                          </button>
                          {filteredVendors.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              {departmentFilter ? 'No vendors found for this department' : 'No vendors found'}
                            </div>
                          ) : (
                            filteredVendors.map((vendor) => (
                              <button
                                key={vendor.id}
                                type="button"
                                onClick={() => {
                                  setVendorFilter(vendor.id);
                                  setVendorDropdownOpen(false);
                                  setVendorSearchTerm('');
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                  vendorFilter === vendor.id ? 'bg-primary-50 text-primary-700' : ''
                                }`}
                              >
                                {vendor.name}
                              </button>
                            ))
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* Status Filter */}
              <div className="relative" ref={statusDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="w-full input-field flex items-center justify-between"
                >
                  <span className="truncate">
                    {selectedStatusName}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>
                {statusDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div className="p-2">
                      <input
                        type="text"
                        placeholder="Search status..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={statusSearchTerm}
                        onChange={(e) => setStatusSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {statusOptions
                        .filter((option) =>
                          option.label.toLowerCase().includes(statusSearchTerm.toLowerCase())
                        )
                        .map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setStatusFilter(option.value);
                              setStatusDropdownOpen(false);
                              setStatusSearchTerm('');
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              statusFilter === option.value ? 'bg-primary-50 text-primary-700' : ''
                            }`}
                          >
                    {option.label}
                          </button>
                        ))}
                      {statusOptions.filter((option) =>
                        option.label.toLowerCase().includes(statusSearchTerm.toLowerCase())
                      ).length === 0 && (
                        <div className="px-4 py-2 text-sm text-gray-500 text-center">
                          No status found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* Start Date */}
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                className="input-field"
                value={startDate}
                onChange={(e) => {
                  const nextStart = e.target.value;
                  setStartDate(nextStart);
                  if (nextStart && endDate && endDate <= nextStart) {
                    setEndDate('');
                  }
                }}
              />
              </div>

              {/* End Date */}
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                className="input-field"
                value={endDate}
                min={startDate ? getNextIsoDate(startDate) : undefined}
                onChange={(e) => {
                  const nextEnd = e.target.value;
                  if (nextEnd && startDate && nextEnd <= startDate) {
                    return;
                  }
                  setEndDate(nextEnd);
                }}
              />
              </div>

              {/* Amount Range Slider */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id="enableAmountFilter"
                    checked={amountFilterEnabled}
                    onChange={(e) => setAmountFilterEnabled(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="enableAmountFilter" className="text-sm font-medium text-gray-700">
                    Filter by Maximum Amount
                  </label>
                </div>
                
                {amountFilterEnabled && (
                  <div className="space-y-4">
                    {/* Quick Amount Buttons */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quick Select
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {[10000, 25000, 50000, 100000, 250000, 500000].map((amount) => (
                          <button
                            key={amount}
                            type="button"
                            onClick={() => setMaxAmount(amount)}
                            className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                              maxAmount === amount
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {formatCurrency(amount, 'USD')}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Amount Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="1000000"
                          step="100"
                          value={maxAmount}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 1000000) {
                              setMaxAmount(value);
                            }
                          }}
                          className="input-field pl-7"
                          placeholder="Enter amount"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Enter a value between {formatCurrency(0, 'USD')} and {formatCurrency(1000000, 'USD')}
                      </p>
                    </div>

                    {/* Range Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Or use slider
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1000000"
                        step="1000"
                        value={maxAmount}
                        onChange={(e) => setMaxAmount(Number(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                        style={{
                          background: `linear-gradient(to right, #424ee6 0%, #424ee6 ${(maxAmount / 1000000) * 100}%, #E5E7EB ${(maxAmount / 1000000) * 100}%, #E5E7EB 100%)`
                        }}
                      />
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{formatCurrency(0, 'USD')}</span>
                        <span className="font-semibold text-primary-600">
                          {formatCurrency(maxAmount, 'USD')}
                        </span>
                        <span>{formatCurrency(1000000, 'USD')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col space-y-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="btn-secondary w-full"
                >
                  Clear All Filters
                </button>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="btn-primary w-full"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="card-body">
          {!loading && invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No invoices found matching your filters.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto relative">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  {loading && invoices.length === 0 ? (
                    <tbody className="bg-white">
                      <tr>
                        <td colSpan={6} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center space-y-3">
                            <Spinner size="md" color="blue" />
                            <span className="text-gray-600">Loading invoices...</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(
                      invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.invoice_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.vendor_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invoice.invoice_date).toLocaleDateString()}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${getCurrencyFontClass(invoice.currency || 'USD')}`}>
                          {formatCurrency(invoice.total_amount, invoice.currency || 'USD')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                              invoice.status
                            )}`}
                          >
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1).toLowerCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(invoice)}
                            className="text-primary-600 hover:text-primary-900 flex items-center"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </button>
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                  )}
                </table>
              </div>

              {/* Pagination */}
              <Pagination
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">
                  Invoice Details - {selectedInvoice.invoice_number}
                </h3>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedInvoice(null);
                    setAuditTrailData(null);
                    closeAttachmentViewer();
                  }}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Department</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedInvoice.department_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Vendor</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedInvoice.vendor_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedInvoice.invoice_number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Invoice Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(selectedInvoice.invoice_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Due Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(selectedInvoice.due_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span
                      className={`mt-1 inline-flex px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                        selectedInvoice.status
                      )}`}
                    >
                      {selectedInvoice.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Amounts */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amount (Excluding Vat/Tax)</label>
                    <p className={`mt-1 text-sm text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'USD')}`}>
                      {formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency || 'USD')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Vat/Tax Amount</label>
                    <p className={`mt-1 text-sm text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'USD')}`}>
                      {formatCurrency(selectedInvoice.tax_amount, selectedInvoice.currency || 'USD')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total Amount</label>
                    <p className={`mt-1 text-lg font-bold text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'USD')}`}>
                      {formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency || 'USD')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Paid Amount</label>
                    <p className={`mt-1 text-sm text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'USD')}`}>
                      {formatCurrency(selectedInvoice.paid_amount || 0, selectedInvoice.currency || 'USD')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              {selectedInvoice.line_items && selectedInvoice.line_items.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedInvoice.line_items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.quantity}</td>
                            <td className={`px-4 py-3 text-sm text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'USD')}`}>
                              {formatCurrency(item.unit_price, selectedInvoice.currency || 'USD')}
                            </td>
                            <td className={`px-4 py-3 text-sm text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'USD')}`}>
                              {formatCurrency(item.amount, selectedInvoice.currency || 'USD')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Attachments */}
              {(selectedInvoice.attachment_url || 
                (selectedInvoice.additional_attachments && selectedInvoice.additional_attachments.length > 0)) && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h4>
                  <div className="space-y-3">
                    {selectedInvoice.attachment_url && (
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="w-8 h-8 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center mr-3">
                            <FileText className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">Main Attachment</p>
                            <p className="text-xs text-gray-500">Invoice Document</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleViewAttachment(selectedInvoice.attachment_url || '', 'Main Attachment', true)}
                          disabled={loadingAttachment}
                          className="ml-3 px-3 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 hover:text-primary-800 transition-colors duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingAttachment ? (
                            <>
                              <Spinner size="sm" color="blue" />
                              <span className="ml-1">Loading...</span>
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-1" />
                              <span>View</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    {selectedInvoice.additional_attachments?.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="w-8 h-8 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center mr-3">
                            <FileText className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                            <p className="text-xs text-gray-500">Click to view</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleViewAttachment(att.url || att.filepath, att.filename, false)}
                          disabled={loadingAttachment}
                          className="ml-3 px-3 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 hover:text-primary-800 transition-colors duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingAttachment ? (
                            <>
                              <Spinner size="sm" color="blue" />
                              <span className="ml-1">Loading...</span>
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-1" />
                              <span>View</span>
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invoice Status Trail */}
              {auditTrailData && auditTrailData.audit_trail && auditTrailData.audit_trail.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status</h4>

                  <div className="relative">
                    <div className="space-y-3">
                      {auditTrailData.audit_trail
                        .slice()
                        .reverse()
                        .map((trail: any, index: number, reversedArray: any[]) => {
                          // Check if this trail item contains "Rejected" keyword
                          const statusText = trail.action === 'UPDATE' && trail.description.includes('Status changed')
                            ? trail.details.new_status
                            : trail.description;
                          const isRejected = statusText && statusText.toLowerCase().includes('rejected');
                          
                          return (
                          <div key={trail.id} className="relative flex items-start">
                            {/* Timeline line segment - connects to next item (below current item) */}
                            {index < reversedArray.length - 1 && (
                              <div className={`absolute left-3 top-6 w-0.5 z-0 ${isRejected ? 'bg-red-600' : 'bg-green-300'}`} style={{ height: 'calc(100% + 0.75rem)' }}></div>
                            )}
                            {/* Timeline dot */}
                            <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white shadow-sm ${isRejected ? 'bg-red-600' : 'bg-green-600'}`}>
                            </div>

                            {/* Content */}
                            <div className={`ml-3 flex-1 rounded-md p-3 border ${isRejected ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-sm font-semibold capitalize ${isRejected ? 'text-red-900' : 'text-gray-900'}`}>
                                  {statusText}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(trail.performed_at).toLocaleString()}
                                </span>
                              </div>

                              <div className="text-xs text-gray-600 mb-1">
                                By: {trail.performed_by.name} ({trail.performed_by.email})
                              </div>

                              {/* Show approver details when approvers are assigned */}
                              {trail.action === 'UPDATE' && trail.description === 'Approvers assigned' && trail.details && (
                                <div className="space-y-0.5">
                                  {trail.details.approver_1?.new && (
                                    <div className="text-xs text-gray-600">
                                      <span className="font-medium">Finance Team Approver:</span> {trail.details.approver_1.new.name}
                                    </div>
                                  )}
                                  {trail.details.approver_2?.new && (
                                    <div className="text-xs text-gray-600">
                                      <span className="font-medium">Approver 2:</span> {trail.details.approver_2.new.name}
                                    </div>
                                  )}
                                  {trail.details.approver_3?.new && (
                                    <div className="text-xs text-gray-600">
                                      <span className="font-medium">Approver 3:</span> {trail.details.approver_3.new.name}
                                    </div>
                                  )}
                                  {trail.details.payment_approver?.new && (
                                    <div className="text-xs text-gray-600">
                                      <span className="font-medium">Payment Approver:</span> {trail.details.payment_approver.new.name}
                                    </div>
                                  )}
                                  {trail.details.payment_attachement && (
                                    <div className="text-xs text-gray-600 flex items-center gap-2">
                                      <span className="font-medium">Payment Attachment:</span> {trail.details.payment_attachement.new.filepath}
                                    </div>
                                  )}
                                </div>
                              )}

                              {(trail.details.paid_amount || trail.details.reference_number || trail.details.payment_date || trail.details.payment_notes) && (
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-1 gap-2">
                                  {trail.details.paid_amount && (
                                    <div className="flex flex-col bg-green-50 rounded border-l-4 border-green-400 px-3 py-2">
                                      <span className="text-xs text-green-700 font-semibold">Paid Amount</span>
                                      <span className="text-sm text-green-900">{trail.details.paid_amount}</span>
                                    </div>
                                  )}
                                  {trail.details.reference_number && (
                                    <div className="flex flex-col bg-yellow-50 rounded border-l-4 border-yellow-400 px-3 py-2">
                                      <span className="text-xs text-yellow-700 font-semibold">Reference Number</span>
                                      <span className="text-sm text-yellow-900">{trail.details.reference_number}</span>
                                    </div>
                                  )}
                                  {trail.details.payment_date && (
                                    <div className="flex flex-col bg-primary-50 rounded border-l-4 border-primary-400 px-3 py-2">
                                      <span className="text-xs text-primary-700 font-semibold">Payment Date</span>
                                      <span className="text-sm text-primary-900">
                                        {trail.details.payment_date ? new Date(trail.details.payment_date).toLocaleDateString() : ''}
                                      </span>
                                    </div>
                                  )}
                                  {trail.details.notes && (
                                    <div className="flex flex-col bg-gray-50 rounded border-l-4 border-gray-400 px-3 py-2">
                                      <span className="text-xs text-gray-700 font-semibold">Notes</span>
                                      <span className="text-sm text-gray-900">{trail.details.notes}</span>
                                    </div>
                                  )}
                                  {trail.details.payment_attachement?.attachment_url && (
                                    <button
                                      type="button"
                                      onClick={() => handleViewAttachment(
                                        trail.details.payment_attachement.attachment_url,
                                        trail.details.payment_attachement.filename || 'Payment Attachment',
                                        false
                                      )}
                                      className="flex flex-col bg-gray-50 rounded border-l-4 border-gray-400 px-3 py-2 text-left hover:bg-gray-100"
                                      title="View payment attachment"
                                    >
                                      <span className="text-xs text-gray-700 font-semibold">Payment Attachment</span>
                                      <span className="text-sm text-primary-600 underline truncate">{trail.details.payment_attachement.filename}</span>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      {showAttachmentViewer && currentAttachmentUrl && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-white" />
                <h3 className="text-xl font-semibold text-white truncate">
                  {currentAttachmentName}
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                {originalAttachmentUrl && (
                  <button
                    onClick={() => {
                      window.open(originalAttachmentUrl, '_blank');
                    }}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Open in new tab"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                )}
                <a
                  href={currentAttachmentUrl}
                  download={currentAttachmentName}
                        target="_blank"
                        rel="noopener noreferrer"
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="h-5 w-5" />
                </a>
                <button
                  onClick={closeAttachmentViewer}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
                  </div>
                </div>

            {/* Content - iframe */}
            <div className="flex-1 overflow-hidden bg-gray-100 p-4">
              {loadingAttachment ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner size="md" color="blue" />
                  <span className="ml-3 text-gray-600">Loading attachment...</span>
                </div>
              ) : (
                <iframe
                  src={currentAttachmentUrl}
                  className="w-full h-full min-h-[600px] rounded-lg shadow-lg border-0"
                  title={currentAttachmentName}
                  style={{ border: 'none' }}
                  onError={(e) => {
                    console.error('Iframe load error:', e);
                    errorModal('Failed to load in viewer. Opening in new tab...');
                    // Try to extract original URL from blob URL or use currentAttachmentUrl
                    // For blob URLs, we can't get the original, so we'll need to handle this differently
                    setTimeout(() => {
                      // If it's not a blob URL, open it
                      if (!currentAttachmentUrl.startsWith('blob:')) {
                        window.open(currentAttachmentUrl, '_blank');
                      }
                    }, 1000);
                  }}
                  onLoad={() => {
                    // Iframe loaded successfully
                    console.log('Iframe loaded successfully');
                  }}
                />
              )}
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Reports;

